import { apply, celebrate, doChore, onceToday, toast } from '../core/effects';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { say, VOICES, type Voice } from '../audio/speech';
import { completeObjective } from '../content/days';
import { h } from '../ui/dom';
import { popView, pushView, registerTab } from './phone';

type Contact = 'mom' | 'dad' | 'friend' | 'hotline';

const META: Record<Contact, { icon: string; color: string; voice: Voice }> = {
  mom: { icon: '👩', color: '#e74c3c', voice: VOICES.mom },
  dad: { icon: '👨', color: '#3498db', voice: VOICES.dad },
  friend: { icon: '🏀', color: '#27ae60', voice: VOICES.friend },
  hotline: { icon: '👮', color: '#f39c12', voice: VOICES.officer },
};

function contacts() {
  const t = m().phone.callList;
  return {
    title: t.title,
    body: h(
      'div',
      { class: 'ph-list' },
      (Object.keys(META) as Contact[]).map((k) =>
        h(
          'button',
          { class: 'ph-item', onclick: () => call(k) },
          h('span', { class: 'av', style: `background:${META[k].color}` }, META[k].icon),
          h('span', { class: 'info' }, h('b', null, t[k].name), h('small', null, t[k].sub)),
          h('span', { class: 'tm call-ico' }, '📞'),
        ),
      ),
    ),
  };
}

interface CallState {
  who: Contact;
  connected: boolean;
  line: string;
  replies: { text: string; run: () => void }[];
}

let state: CallState | null = null;
let timer = 0;

function call(who: Contact): void {
  sfx('ring');
  state = { who, connected: false, line: m().phone.call.connecting, replies: [] };
  pushView(view);
  window.clearTimeout(timer);
  timer = window.setTimeout(() => connect(who), 1300);
}

function respond(text: string, voice: Voice): void {
  if (!state) return;
  say(text, voice);
  state.line = text;
  state.replies = [];
  pushView(view);
}

function connect(who: Contact): void {
  if (!state || state.who !== who) return;
  const c = m().phone.call;
  const v = META[who].voice;
  sfx('ok');
  state.connected = true;
  if (who === 'mom') {
    state.line = c.mom.line;
    state.replies = [
      { text: c.mom.a, run: () => momReply(m().phone.call.mom.ra) },
      { text: c.mom.b, run: () => momReply(m().phone.call.mom.rb) },
    ];
  } else if (who === 'dad') {
    const r = () => {
      const first = doChore('callDad');
      if (first) {
        celebrate();
        apply({ mood: 10, love: { dad: 15, mom: 10 } });
        completeObjective('callDad');
      }
      respond(m().phone.call.dad.reply, v);
    };
    state.line = c.dad.line;
    state.replies = [
      { text: c.dad.a, run: r },
      { text: c.dad.b, run: r },
    ];
  } else if (who === 'friend') {
    const r = () => {
      if (onceToday('call.friendOut')) apply({ mood: 8, love: { emma: 5 } });
      respond(m().phone.call.friend.reply, v);
    };
    state.line = c.friend.line;
    state.replies = [
      { text: c.friend.a, run: r },
      { text: c.friend.b, run: r },
    ];
  } else {
    state.line = c.hotline.line;
    state.replies = [
      {
        text: c.hotline.a,
        run: () => {
          if (onceToday('call.hotline')) {
            celebrate();
            apply({ stars: 5 });
          }
          respond(m().phone.call.hotline.reply, v);
        },
      },
    ];
  }
  say(state.line, v);
  pushView(view);
}

function momReply(text: string): void {
  if (onceToday('call.momOut')) apply({ mood: 8, love: { mom: 12 } });
  else toast('❤️');
  respond(text, VOICES.mom);
}

function hangUp(): void {
  window.clearTimeout(timer);
  state = null;
  sfx('click');
  popView();
}

function view() {
  const c = m().phone.call;
  const s = state!;
  const name = m().phone.callList[s.who].name;
  return {
    title: name,
    back: hangUp,
    body: h(
      'div',
      { class: 'call-panel' },
      h('div', { class: `call-avatar${s.connected ? '' : ' ringing'}`, style: `background:${META[s.who].color}` }, META[s.who].icon),
      h('div', { class: 'call-name' }, name),
      h('div', { class: 'call-status' }, s.connected ? c.inCall : c.connecting),
      h('div', { class: 'call-dialogue' }, s.connected ? `“${s.line}”` : '…'),
      h(
        'div',
        { class: 'call-actions' },
        s.replies.map((r) => h('button', { class: 'btn green', onclick: () => (sfx('click'), r.run()) }, r.text)),
        h('button', { class: 'btn red', onclick: hangUp }, c.hangUp),
      ),
    ),
  };
}

export function initCallsTab(): void {
  registerTab('calls', contacts);
}
